<template>
  <div>
    <v-container fluid>
      <!-- ── Header (V2 family house style: primary toolbar + today on the right) ── -->
      <v-skeleton-loader
        :loading="!flagNav"
        transition="scale-transition"
        max-height="64"
        type="table-heading"
      >
        <v-toolbar color="primary" class="header-title text-white" data-testid="mcp-toolbar">
          <v-toolbar-title class="title">MCP Connection</v-toolbar-title>
          <v-spacer />
          <div class="d-flex pr-4">
            <v-toolbar-title class="title">{{ today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>

      <!-- ── คำอธิบาย ────────────────────────────────────────────────────── -->
      <v-card class="card2" data-testid="mcp-intro-card">
        <v-card-text class="pa-4">
          <div class="d-flex align-center flex-wrap ga-2">
            <v-icon color="primary" class="mr-1" size="26">mdi-connection</v-icon>
            <h3 class="text-h6 font-weight-bold text-primary mb-0">โทเคนสำหรับต่อ MCP</h3>
            <v-chip size="small" color="primary" variant="tonal" label prepend-icon="mdi-shield-account">
              เฉพาะผู้ดูแลระบบ
            </v-chip>
          </div>
          <p class="text-caption text-medium-emphasis mt-2 mb-0">
            ออกโทเคนส่วนตัวเพื่อให้เครื่องมือ AI (Claude Code / Claude Desktop) ต่อเข้ามาอ่านข้อมูล
            Tax Planning V2 ของ environment นี้ผ่าน MCP ได้โดยตรง —
            <strong>โทเคนผูกกับบัญชีของคุณ</strong> สิทธิ์ที่ได้จึงเท่ากับสิทธิ์ของคุณเอง
            และ<strong>ค่าโทเคนจะแสดงเพียงครั้งเดียว</strong>ตอนออก (ระบบเก็บแต่ค่า hash)
          </p>
        </v-card-text>
      </v-card>

      <!-- ── สวิตช์ระดับ environment ──────────────────────────────────────
           อยู่คนละที่กับโทเคน: 4 คีย์นี้เป็นแถวใน cus.SystemConfig หมวด MCP ที่ WebAPI/Mcp/McpGate.cs
           อ่านใหม่ทุก request (ตั้งใจไม่ cache) ⇒ บันทึกแล้วมีผลทันที ไม่ต้อง restart backend
           🔴 แก้ได้เฉพาะ role 0 — หน้านี้ทั้งหน้าเปิดถึง role 1 ⇒ ต้องมีทางลงที่อ่านออกเมื่อโดน 401 -->
      <v-card class="card2 mt-4" data-testid="mcp-settings-card">
        <v-card-title class="section-title">
          <v-icon size="20" color="primary" class="mr-2">mdi-tune-variant</v-icon>
          สวิตช์ของ MCP (มีผลทั้ง environment นี้)
          <v-spacer />
          <v-btn size="small" variant="text" :loading="settings.loading" @click="loadSettings">
            <v-icon start size="16">mdi-refresh</v-icon>รีเฟรช
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-4 pt-0">
          <v-alert
            v-if="settings.forbidden"
            type="info"
            variant="tonal"
            density="comfortable"
            data-testid="mcp-settings-forbidden"
          >
            สวิตช์ชุดนี้แก้ได้เฉพาะ<strong>ผู้ดูแลระบบสูงสุด</strong> — บัญชีของคุณออก/ถอนโทเคนได้ตามปกติ
            แต่แก้ค่าระดับ environment ไม่ได้
          </v-alert>

          <v-alert
            v-else-if="!settings.loading && !settings.found"
            type="warning"
            variant="tonal"
            density="comfortable"
            data-testid="mcp-settings-missing"
          >
            ยังไม่มีแถวค่าตั้งของ MCP ในฐานข้อมูล — ต้องรัน <code>docs/sql/20260904/McpSystemConfig.sql</code>
            ก่อนถึงจะแก้จากหน้านี้ได้
          </v-alert>

          <template v-if="!settings.forbidden">
            <v-row dense align="start">
              <v-col cols="12" md="4">
                <v-switch
                  v-model="settings.enabled"
                  color="primary"
                  hide-details
                  density="comfortable"
                  :disabled="!settings.found"
                  data-testid="mcp-enabled-switch"
                  :label="settings.enabled ? 'เปิดใช้งาน MCP' : 'ปิด MCP ทั้ง environment'"
                />
                <div class="text-caption text-medium-emphasis">
                  ปิด = ปฏิเสธทุกคำขอที่เข้ามาทาง MCP ตั้งแต่ตอนเชื่อมต่อครั้งแรก (initialize)
                </div>
              </v-col>

              <v-col cols="12" md="4">
                <v-switch
                  v-model="settings.allowWrite"
                  color="warning"
                  hide-details
                  density="comfortable"
                  :disabled="!settings.found"
                  data-testid="mcp-allow-write-switch"
                  :label="settings.allowWrite ? 'ให้ tool ที่เขียนข้อมูลทำงานได้' : 'เปิดเฉพาะ tool ที่อ่านอย่างเดียว'"
                />
                <div class="text-caption text-medium-emphasis">
                  ปิด = tool ฝั่งเขียนไม่โผล่ใน tools/list เลย · ต้องเปิดคู่กับโทเคนที่เป็น
                  “อ่าน + เขียน” ในตารางด้านล่าง ขาดข้างใดข้างหนึ่งก็ไม่โผล่
                </div>
              </v-col>

              <v-col cols="12" md="4" class="d-flex align-center">
                <v-btn
                  color="primary"
                  variant="elevated"
                  block
                  :loading="settings.saving"
                  :disabled="!settings.found"
                  data-testid="mcp-settings-save-btn"
                  @click="saveSettings"
                >
                  <v-icon start>mdi-content-save</v-icon>
                  บันทึกสวิตช์
                </v-btn>
              </v-col>
            </v-row>

            <v-row dense class="mt-2">
              <v-col cols="12" md="8">
                <v-text-field
                  v-model="settings.enabledTools"
                  label="tool ที่เปิดใช้ (JSON array)"
                  placeholder='["mcp_ping","tax_log_sessions"]'
                  variant="outlined"
                  density="comfortable"
                  hide-details="auto"
                  :disabled="!settings.found"
                  data-testid="mcp-enabled-tools-input"
                />
                <div class="text-caption text-medium-emphasis mt-1">
                  เว้นว่าง = เปิดทุก tool ที่ระบบลงทะเบียนไว้ · <code>[]</code> = ไม่เปิดสักตัว —
                  สองอันนี้คนละความหมาย ระวังอย่าสลับกัน
                </div>
              </v-col>

              <v-col cols="12" md="4">
                <v-text-field
                  v-model="settings.protocolVersions"
                  label="เวอร์ชันของ endpoint ที่เปิด"
                  placeholder='["v1"]'
                  variant="outlined"
                  density="comfortable"
                  hide-details="auto"
                  :disabled="!settings.found"
                  data-testid="mcp-protocol-versions-input"
                />
                <div class="text-caption text-medium-emphasis mt-1">
                  เว้นว่าง = <code>["v1"]</code> ตามค่าเริ่มต้น
                </div>
              </v-col>
            </v-row>

            <v-alert
              v-if="settings.found && settings.allowWrite"
              type="warning"
              variant="tonal"
              density="comfortable"
              class="mt-4"
              data-testid="mcp-settings-write-warning"
            >
              สวิตช์ฝั่งเขียนเปิดอยู่ — เครื่องมือ AI ที่ถือโทเคนแบบ “อ่าน + เขียน” แก้ config ของระบบได้จริง
              (ทุก tool เริ่มที่ dry-run ต้องสั่งยืนยันก่อนถึงเขียนลงฐานข้อมูล)
            </v-alert>
          </template>
        </v-card-text>
      </v-card>

      <!-- ── ออกโทเคนใหม่ ────────────────────────────────────────────────── -->
      <v-card class="card2 mt-4" data-testid="mcp-issue-card">
        <v-card-title class="section-title">
          <v-icon size="20" color="primary" class="mr-2">mdi-key-plus</v-icon>
          ออกโทเคนใหม่
        </v-card-title>
        <v-card-text class="pa-4 pt-0">
          <v-row dense align="start">
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.tokenName"
                label="ชื่อเรียกโทเคน"
                variant="outlined"
                density="comfortable"
                hide-details="auto"
                counter="200"
                maxlength="200"
                :error-messages="nameError"
                data-testid="mcp-name-input"
                @keyup.enter="issue"
                @update:model-value="nameError = ''"
              />
              <div class="text-caption text-medium-emphasis mt-1">
                ชื่อคือสิ่งเดียวที่ใช้ไล่ได้ว่าใบไหนคือใบไหนตอนจะถอน (ค่าเต็มอ่านซ้ำไม่ได้)
              </div>
            </v-col>

            <v-col cols="12" md="4">
              <v-switch
                v-model="form.allowWrite"
                color="warning"
                hide-details
                density="comfortable"
                data-testid="mcp-write-switch"
                :label="form.allowWrite ? 'ให้สร้าง/แก้ข้อมูลได้' : 'อ่านอย่างเดียว'"
              />
              <div class="text-caption text-medium-emphasis">
                เปิดเฉพาะเมื่อต้องให้ AI แก้ config หรือคุยกับ Playground จริง ๆ
              </div>
            </v-col>

            <v-col cols="12" md="2" class="d-flex align-center">
              <v-btn
                color="primary"
                variant="elevated"
                block
                :loading="issuing"
                data-testid="mcp-issue-btn"
                @click="issue"
              >
                <v-icon start>mdi-plus</v-icon>
                ออกโทเคน
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- ── โทเคนที่เพิ่งออก (แสดงครั้งเดียว) ───────────────────────────── -->
      <v-card v-if="issued" class="card2 mt-4 issued-card" data-testid="mcp-issued-card">
        <v-card-title class="section-title">
          <v-icon size="20" color="success" class="mr-2">mdi-check-circle</v-icon>
          ออกโทเคน "{{ issued.tokenName }}" แล้ว
        </v-card-title>
        <v-card-text class="pa-4 pt-0">
          <v-alert type="warning" variant="tonal" density="comfortable" class="mb-4" data-testid="mcp-once-warning">
            <strong>ค่านี้แสดงครั้งเดียว</strong> — ปิดหรือรีเฟรชหน้านี้แล้วอ่านซ้ำไม่ได้อีก
            (ระบบเก็บแต่ hash ไม่ได้เก็บค่าเต็ม) ถ้าทำหาย ให้ถอนใบนี้แล้วออกใบใหม่
          </v-alert>

          <div class="token-display-container">
            <label class="token-label">โทเคนของคุณ</label>
            <div class="token-input-wrapper">
              <input
                :value="issued.token"
                readonly
                class="token-input"
                data-testid="mcp-token-input"
                @focus="$event.target.select()"
              />
              <v-btn icon variant="text" size="small" class="copy-btn" title="คัดลอก" @click="copy(issued.token, 'โทเคน')">
                <v-icon size="18">mdi-content-copy</v-icon>
              </v-btn>
            </div>
          </div>

          <!-- Claude Code -->
          <div class="snippet-block mt-4">
            <div class="snippet-head">
              <span class="snippet-title">Claude Code</span>
              <v-btn size="small" variant="text" color="primary" @click="copy(claudeCodeCommand, 'คำสั่ง Claude Code')">
                <v-icon start size="16">mdi-content-copy</v-icon>คัดลอก
              </v-btn>
            </div>
            <pre class="snippet-body" data-testid="mcp-claude-code">{{ claudeCodeCommand }}</pre>
          </div>

          <!-- Claude Desktop -->
          <div class="snippet-block mt-3">
            <div class="snippet-head">
              <span class="snippet-title">Claude Desktop (claude_desktop_config.json)</span>
              <v-btn size="small" variant="text" color="primary" @click="copy(claudeDesktopConfig, 'ค่าตั้ง Claude Desktop')">
                <v-icon start size="16">mdi-content-copy</v-icon>คัดลอก
              </v-btn>
            </div>
            <pre class="snippet-body" data-testid="mcp-claude-desktop">{{ claudeDesktopConfig }}</pre>
          </div>

          <div class="text-caption text-medium-emphasis mt-3">
            URL ด้านบนคือ <strong>API</strong> ของ environment นี้ ({{ origin }}) ซึ่งอาจไม่ใช่ที่อยู่เดียวกับหน้าเว็บ
            ที่คุณเปิดอยู่ — โทเคนใบหนึ่งจะจำกัด environment ก็ต่อเมื่อมีการตั้งไว้ ถ้าไม่จำกัดก็ใช้ได้ทุกที่
            ที่ deploy โค้ดชุดเดียวกันและใช้ฐานข้อมูลเดียวกัน
          </div>
        </v-card-text>
      </v-card>

      <!-- ── รายการโทเคน ─────────────────────────────────────────────────── -->
      <v-card class="card2 mt-4" data-testid="mcp-list-card">
        <v-card-title class="section-title">
          <v-icon size="20" color="primary" class="mr-2">mdi-format-list-bulleted</v-icon>
          โทเคนที่ออกไว้
          <v-spacer />
          <v-btn size="small" variant="text" :loading="loading" @click="reload">
            <v-icon start size="16">mdi-refresh</v-icon>รีเฟรช
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <v-table density="comfortable" data-testid="mcp-token-table">
            <thead>
              <tr>
                <th>ชื่อเรียก</th>
                <th>สิทธิ์</th>
                <th>สถานะ</th>
                <th>ออกเมื่อ</th>
                <th>ใช้ล่าสุด</th>
                <th class="text-right">จัดการ</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="!loading && tokens.length === 0">
                <td colspan="6" class="text-center text-medium-emphasis py-6">
                  ยังไม่มีโทเคน — ออกใบแรกได้จากกล่องด้านบน
                </td>
              </tr>
              <tr v-for="t in tokens" :key="t.id" :class="{ 'row-revoked': !t.isUsable }">
                <td>
                  <div class="font-weight-medium">{{ t.tokenName }}</div>
                  <div class="text-caption text-medium-emphasis">…{{ t.tokenSuffix }}</div>
                </td>
                <td>
                  <v-chip size="x-small" label :color="t.allowWrite ? 'warning' : 'primary'" variant="tonal">
                    {{ t.allowWrite ? 'อ่าน + เขียน' : 'อ่านอย่างเดียว' }}
                  </v-chip>
                </td>
                <td>
                  <v-chip size="x-small" label :color="t.isUsable ? 'success' : 'error'" variant="tonal">
                    {{ statusLabel(t) }}
                  </v-chip>
                </td>
                <td class="text-caption">{{ fmtThaiDateTime(t.createDate) }}</td>
                <td class="text-caption">
                  {{ t.lastUsedDate ? fmtThaiDateTime(t.lastUsedDate) : 'ยังไม่เคยใช้' }}
                </td>
                <td class="text-right">
                  <v-btn
                    v-if="t.isUsable"
                    size="small"
                    variant="text"
                    color="error"
                    :data-testid="`mcp-revoke-${t.id}`"
                    @click="askRevoke(t)"
                  >
                    <v-icon start size="16">mdi-cancel</v-icon>ถอน
                  </v-btn>
                  <span v-else class="text-caption text-medium-emphasis">
                    {{ t.revokedBy ? `ถอนโดย ${t.revokedBy}` : '—' }}
                  </span>
                </td>
              </tr>
            </tbody>
          </v-table>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- ── ยืนยันการถอน ──────────────────────────────────────────────────── -->
    <v-dialog v-model="revokeDialog" max-width="460">
      <v-card>
        <v-card-title class="text-h6">ถอนโทเคนนี้?</v-card-title>
        <v-card-text>
          <p class="mb-2">
            <strong>{{ revokeTarget && revokeTarget.tokenName }}</strong>
            (…{{ revokeTarget && revokeTarget.tokenSuffix }})
          </p>
          <p class="text-body-2 mb-0">
            มีผลทันที — เครื่องมือที่ใช้โทเคนใบนี้อยู่จะต่อไม่ได้ทันทีที่กดยืนยัน และย้อนกลับไม่ได้
            (ต้องออกใบใหม่แทน)
          </p>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="revokeDialog = false">ยกเลิก</v-btn>
          <v-btn color="error" variant="elevated" :loading="revoking" @click="confirmRevoke">ถอนโทเคน</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snack.show" :color="snack.color" timeout="2500" location="top">
      {{ snack.text }}
    </v-snackbar>
  </div>
</template>

<script>
/**
 * MCP Connection — ออก/ดู/ถอน personal access token ของ remote MCP (`/api/mcp/v1`)
 *
 * 🔴 ค่าเต็มของโทเคนอยู่ในหน่วยความจำของหน้านี้เท่านั้น (`issued.token`) และหายไปเมื่อรีเฟรช
 *    ห้ามเก็บลง localStorage / query string / log — backend เก็บแต่ SHA-256 และไม่มี endpoint
 *    ไหนคืนค่านี้ให้อีก · ที่หน้าจอเก็บไว้ก็เพื่อประกอบคำสั่ง 2 ชุดให้คัดลอกได้ในคราวเดียว
 *
 * 🔴 URL ของ endpoint มาจาก **`VITE_APP_API`** (ตัวเดียวกับที่ service อื่นใช้ยิง API) ไม่ใช่
 *    `window.location.origin` — ตอน dev หน้าเว็บอยู่บน Vite dev server (`:8080`) แต่ API อยู่ `:5000`
 *    และบน uat/prod ตัว API ก็เป็นคนละ host กับ SPA (`…-app` vs `…-app-api`)
 *    ⇒ ใช้ origin ของหน้าเว็บจะได้คำสั่งที่ต่อไม่ติด (`ENDPOINT_NOT_FOUND`) ทั้งสองกรณี
 *    fallback เป็น `window.location.origin` เฉพาะตอนไม่มีค่า env (build ที่เสิร์ฟจาก API ตัวเดียวกัน)
 *
 * เวลาแสดงผลใช้ utils/thaiTime.js เพราะ wire ของ backend ส่ง DateTime แบบ **ไม่มี Z**
 * (`new Date(v)` ตรง ๆ จะอ่านเป็นเวลาท้องถิ่นแล้วช้าไป 7 ชั่วโมงเงียบ ๆ)
 */
import { mcpTokenApi } from '../services/mcpTokenApi';
import { mcpSettingsApi, MCP_CONFIG_KEYS } from '../services/mcpSettingsApi';
import { fmtThaiDateTime } from '../utils/thaiTime';

export default {
  name: 'McpConnection',

  data() {
    return {
      flagNav: true,
      today: new Date().toLocaleDateString('th-TH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),

      /**
       * สวิตช์ระดับ environment (cus.SystemConfig หมวด MCP) — คนละชุดกับโทเคนในหน้าเดียวกัน
       * `found=false` = ยังไม่ได้รัน seed SQL · `forbidden=true` = บัญชีนี้ไม่ใช่ role 0 (แก้ไม่ได้ แต่ออกโทเคนได้)
       * `settingsOriginal` เก็บค่าดิบที่อ่านมา ไว้ส่งเฉพาะคีย์ที่เปลี่ยนจริงตอนบันทึก
       */
      settings: {
        loading: false,
        saving: false,
        found: false,
        forbidden: false,
        enabled: false,
        allowWrite: false,
        enabledTools: '',
        protocolVersions: '',
      },
      settingsOriginal: {},

      form: { tokenName: '', allowWrite: false },
      nameError: '',
      issuing: false,

      /** ผลของการออกโทเคนครั้งล่าสุด — ค่าเต็มอยู่ที่นี่ที่เดียว และหายเมื่อรีเฟรช */
      issued: null,

      tokens: [],
      loading: false,

      revokeDialog: false,
      revokeTarget: null,
      revoking: false,

      snack: { show: false, text: '', color: 'success' },
    };
  },

  computed: {
    /** origin ของ **API** (ไม่ใช่ของหน้าเว็บ) — ดูเหตุผลที่หัวไฟล์ */
    origin() {
      const raw = import.meta.env.VITE_APP_API || window.location.origin;
      try {
        return new URL(raw, window.location.origin).origin;
      } catch (e) {
        return String(raw).replace(/\/+$/, '');
      }
    },

    endpoint() {
      return `${this.origin}/api/mcp/v1`;
    },

    /**
     * ชื่อ server ที่เห็นในเครื่องมือ — ตั้งจาก host ของ **API** เพื่อไม่ให้ชนกันเมื่อคนต่อหลาย env พร้อมกัน
     * (เช่น `fusion-tax-dev-chat-lhb-app-api`) · ตอน dev บน localhost จะได้ `fusion-tax-localhost`
     */
    serverName() {
      let host = 'local';
      try {
        host = new URL(this.endpoint).hostname.split('.')[0] || 'local';
      } catch (e) {
        host = 'local';
      }
      return `fusion-tax-${host}`;
    },

    claudeCodeCommand() {
      const token = this.issued ? this.issued.token : 'fcb_...';
      return [
        `claude mcp remove ${this.serverName} -s user`,
        `claude mcp add --transport http --scope user ${this.serverName} \\`,
        `  ${this.endpoint} \\`,
        `  --header "Authorization: Bearer ${token}"`,
      ].join('\n');
    },

    claudeDesktopConfig() {
      const token = this.issued ? this.issued.token : 'fcb_...';
      return JSON.stringify(
        {
          mcpServers: {
            [this.serverName]: {
              type: 'http',
              url: this.endpoint,
              headers: { Authorization: `Bearer ${token}` },
            },
          },
        },
        null,
        2,
      );
    },
  },

  mounted() {
    this.reload();
    this.loadSettings();
  },

  methods: {
    fmtThaiDateTime,

    statusLabel(t) {
      if (t.revokedDate) return 'ถอนแล้ว';
      if (!t.activeFlag) return 'ปิดใช้งาน';
      if (t.expiresAt && !t.isUsable) return 'หมดอายุ';
      return 'ใช้งานได้';
    },

    // ── สวิตช์ระดับ environment ───────────────────────────────────────────────────
    async loadSettings() {
      this.settings.loading = true;
      try {
        const rows = (await mcpSettingsApi.list()) || [];
        const raw = (key) => {
          const row = rows.find((r) => (r.configKey || '').toUpperCase() === key);
          // ConfigValue เป็น NOT NULL ที่ฝั่ง DB แต่ '' คือค่าที่ใช้จริง (= ไม่จำกัด)
          // ⇒ null คืนเฉพาะตอน "ไม่มีแถว" เท่านั้น ซึ่งคนละเรื่องกับ 'ว่าง'
          return row ? String(row.configValue == null ? '' : row.configValue) : null;
        };

        const values = {
          [MCP_CONFIG_KEYS.enabled]: raw(MCP_CONFIG_KEYS.enabled),
          [MCP_CONFIG_KEYS.allowWrite]: raw(MCP_CONFIG_KEYS.allowWrite),
          [MCP_CONFIG_KEYS.enabledTools]: raw(MCP_CONFIG_KEYS.enabledTools),
          [MCP_CONFIG_KEYS.protocolVersions]: raw(MCP_CONFIG_KEYS.protocolVersions),
        };

        // ขาดแถวไหนก็ตาม = ยังไม่ได้รัน seed ⇒ ล็อกการ์ดไว้ ดีกว่าปล่อยให้กดบันทึกแล้วพังทีหลัง
        this.settings.found = Object.values(values).every((v) => v !== null);
        this.settings.forbidden = false;
        this.settings.enabled = this.mcpBool(values[MCP_CONFIG_KEYS.enabled]);
        this.settings.allowWrite = this.mcpBool(values[MCP_CONFIG_KEYS.allowWrite]);
        this.settings.enabledTools = values[MCP_CONFIG_KEYS.enabledTools] || '';
        this.settings.protocolVersions = values[MCP_CONFIG_KEYS.protocolVersions] || '';
        this.settingsOriginal = values;
      } catch (e) {
        // 401/403 = บัญชีนี้ไม่ใช่ role 0 ซึ่งเป็นเรื่องปกติของหน้านี้ (โทเคนเปิดถึง role 1)
        // ⇒ บอกเป็นข้อความในการ์ด ไม่ใช่เด้ง toast แดงเหมือนระบบพัง
        if (e.httpStatus === 401 || e.httpStatus === 403) {
          this.settings.forbidden = true;
        } else {
          this.toast(e.message || 'โหลดค่าตั้งของ MCP ไม่สำเร็จ', 'error');
        }
        this.settings.found = false;
      } finally {
        this.settings.loading = false;
      }
    },

    async saveSettings() {
      const tools = (this.settings.enabledTools || '').trim();
      const versions = (this.settings.protocolVersions || '').trim();
      if (!this.isJsonArray(tools) || !this.isJsonArray(versions)) {
        this.toast('ช่องที่เป็น JSON array ต้องเป็นรูปแบบ ["ชื่อ"] หรือเว้นว่างเท่านั้น', 'error');
        return;
      }

      const next = {
        [MCP_CONFIG_KEYS.enabled]: this.settings.enabled ? 'true' : 'false',
        [MCP_CONFIG_KEYS.allowWrite]: this.settings.allowWrite ? 'true' : 'false',
        [MCP_CONFIG_KEYS.enabledTools]: tools,
        [MCP_CONFIG_KEYS.protocolVersions]: versions,
      };

      // ส่งเฉพาะคีย์ที่เปลี่ยนจริง — คีย์ที่ไม่ได้แตะจะได้ไม่ถูกเขียนทับด้วยค่าที่ผ่าน trim มาแล้ว
      const changed = Object.keys(next).filter((key) => next[key] !== this.settingsOriginal[key]);
      if (changed.length === 0) {
        this.toast('ค่ายังเหมือนเดิม ไม่มีอะไรต้องบันทึก', 'info');
        return;
      }

      this.settings.saving = true;
      try {
        // endpoint รับครั้งละคีย์ ⇒ ยิงเรียงกัน เพื่อให้ error บอกได้ว่าคีย์ไหนไม่ผ่าน
        for (const key of changed) {
          await mcpSettingsApi.update(key, next[key]);
        }
        this.toast('บันทึกแล้ว — มีผลทันที (ฝั่งเครื่องมือ AI ต้อง reconnect ถึงจะเห็นรายการ tool ที่เปลี่ยน)');
      } catch (e) {
        this.toast(e.message || 'บันทึกค่าตั้งของ MCP ไม่สำเร็จ', 'error');
      } finally {
        this.settings.saving = false;
        // อ่านกลับเสมอไม่ว่าสำเร็จหรือไม่ — จอต้องไม่ค้างค่าที่ยังไม่ได้เขียนลงฐานข้อมูล
        await this.loadSettings();
      }
    },

    /** 'true'/'1' เท่านั้นที่นับว่าเปิด — ให้ตรงกับ McpGate.ParseBool (ค่าที่แปลไม่ออก = ปิด) */
    mcpBool(rawValue) {
      const value = (rawValue || '').trim().toLowerCase();
      return value === 'true' || value === '1';
    },

    /** ว่าง = "ไม่จำกัด" ถือว่าถูกต้อง · '[]' = "ไม่เปิดสักตัว" ก็ผ่าน — ทั้งคู่เป็นค่าที่ตั้งใจได้ */
    isJsonArray(rawValue) {
      if (rawValue === '') return true;
      try {
        const parsed = JSON.parse(rawValue);
        return Array.isArray(parsed) && parsed.every((item) => typeof item === 'string');
      } catch (e) {
        return false;
      }
    },

    async reload() {
      this.loading = true;
      try {
        this.tokens = (await mcpTokenApi.list()) || [];
      } catch (e) {
        this.toast(e.message || 'โหลดรายการโทเคนไม่สำเร็จ', 'error');
      } finally {
        this.loading = false;
      }
    },

    async issue() {
      const name = (this.form.tokenName || '').trim();
      if (!name) {
        // ตรวจฝั่งหน้าจอด้วย เพื่อไม่ให้ต้องรอ round-trip แค่เพื่อรู้ว่าลืมใส่ชื่อ
        // (ด่านจริงอยู่ที่ BAL — ชื่อว่างถูกปฏิเสธที่นั่นเสมอ)
        this.nameError = 'ต้องตั้งชื่อโทเคน เพื่อให้ไล่ได้ว่าใบไหนคือใบไหนตอนจะถอน';
        return;
      }

      this.issuing = true;
      try {
        const result = await mcpTokenApi.issue({ tokenName: name, allowWrite: this.form.allowWrite });
        this.issued = result;
        this.form.tokenName = '';
        this.form.allowWrite = false;
        this.toast('ออกโทเคนแล้ว — คัดลอกเก็บไว้ก่อนปิดหน้านี้');
        await this.reload();
      } catch (e) {
        this.toast(e.message || 'ออกโทเคนไม่สำเร็จ', 'error');
      } finally {
        this.issuing = false;
      }
    },

    askRevoke(t) {
      this.revokeTarget = t;
      this.revokeDialog = true;
    },

    async confirmRevoke() {
      if (!this.revokeTarget) return;
      this.revoking = true;
      try {
        await mcpTokenApi.revoke(this.revokeTarget.id);

        // ถ้าถอนใบที่เพิ่งออก ให้เอาค่าเต็มออกจากหน้าจอทันที — ไม่มีเหตุผลให้มันค้างอยู่อีก
        if (this.issued && this.issued.id === this.revokeTarget.id) {
          this.issued = null;
        }

        this.revokeDialog = false;
        this.revokeTarget = null;
        this.toast('ถอนโทเคนแล้ว');
        await this.reload();
      } catch (e) {
        this.toast(e.message || 'ถอนโทเคนไม่สำเร็จ', 'error');
      } finally {
        this.revoking = false;
      }
    },

    async copy(text, label) {
      try {
        await navigator.clipboard.writeText(text);
        this.toast(`คัดลอก${label ? ' ' + label : ''}แล้ว`);
      } catch (e) {
        this.toast('คัดลอกไม่สำเร็จ — กดเลือกข้อความแล้วคัดลอกเองได้', 'error');
      }
    },

    toast(text, color = 'success') {
      this.snack = { show: true, text, color };
    },
  },
};
</script>

<style scoped>
/* 🔴 global reset ของโปรเจกต์ล้าง font-weight ของ heading ทิ้ง ⇒ ต้องกำหนดเองทุกครั้ง */
.section-title {
  display: flex;
  align-items: center;
  font-size: 1rem;
  font-weight: 700;
}

.card2 {
  border-radius: 12px;
}

.issued-card {
  border: 1px solid rgb(var(--v-theme-success));
}

.token-display-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.token-label {
  font-size: 0.8rem;
  font-weight: 600;
  opacity: 0.75;
}

.token-input-wrapper {
  display: flex;
  align-items: center;
  gap: 4px;
  border: 1px solid rgba(128, 128, 128, 0.35);
  border-radius: 8px;
  padding: 4px 4px 4px 12px;
}

.token-input {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.85rem;
  color: inherit;
}

.copy-btn {
  flex: 0 0 auto;
}

.snippet-block {
  border: 1px solid rgba(128, 128, 128, 0.28);
  border-radius: 8px;
  overflow: hidden;
}

.snippet-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px 4px 12px;
  background: rgba(128, 128, 128, 0.08);
}

.snippet-title {
  font-size: 0.8rem;
  font-weight: 600;
}

.snippet-body {
  margin: 0;
  padding: 12px;
  /* คำสั่งยาวเกินจอต้องเลื่อนในกล่องของตัวเอง ไม่ใช่ดันทั้งหน้าให้เลื่อนแนวนอน */
  overflow-x: auto;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.78rem;
  line-height: 1.5;
  white-space: pre;
}

.row-revoked {
  opacity: 0.6;
}
</style>
